<script setup lang="ts">
const lastUpdated = "May 19, 2026";

const dataCategories = [
  {
    title: "Account data",
    items: ["Name", "Email address", "Country", "Profile avatar", "User role"],
  },
  {
    title: "Profile and entity data",
    items: [
      "Fighter profiles, including name, nickname, sex/gender, date of birth, country, height, weight, martial arts, fight records, gym association, and avatar",
      "Gym profiles, including name, address, city, country, website, social links, martial arts, members, and avatar",
      "Organisation/promoter profiles, including name, address, country, website, social links, members, and avatar",
    ],
  },
  {
    title: "Fight offer and communication data",
    items: [
      "Fight offers, applications, counter offers, accept/decline actions, and related terms",
      "Messages and chats in the action center",
      "Guest invite email addresses where a private offer is sent to someone who does not yet have an account",
    ],
  },
  {
    title: "Verification data",
    items: [
      "Verification requests",
      "Verification documents",
      "Notes and status updates related to verification",
      "Entity data submitted for verification",
    ],
  },
  {
    title: "Technical and analytics data",
    items: [
      "Login/session data",
      "Device and push notification tokens",
      "Error logs",
      "Product analytics events",
      "Basic technical information such as device/browser information and usage activity",
    ],
  },
];

const useCases = [
  "Create and manage user accounts",
  "Provide fighter, gym, and organisation profiles",
  "Enable fight offers, applications, counters, and messaging",
  "Send email and push notifications",
  "Verify fighters, gyms, and organisations",
  "Prevent abuse and protect account security",
  "Improve the app and understand usage through analytics",
  "Debug errors and maintain the service",
  "Comply with legal obligations",
];

const legalBases = [
  "Contract: to provide the Fight Search service.",
  "Legitimate interests: to secure the app, prevent abuse, send relevant service notifications, improve the product, and debug errors.",
  "Consent: where required, such as certain analytics or notification permissions.",
  "Legal obligation: where we must retain or disclose data to comply with applicable law.",
];

const providers = [
  "Supabase, for authentication, database, backend functions, and infrastructure",
  "Backblaze, for file storage",
  "Firebase, for push notifications",
  "PostHog, for analytics and error logging",
  "Stripe, once payments/subscriptions are enabled",
];

const userRights = [
  "Access your personal data",
  "Correct inaccurate data",
  "Delete your data",
  "Restrict processing",
  "Object to processing",
  "Receive a copy of your data",
  "Withdraw consent where processing is based on consent",
  "Lodge a complaint with a data protection authority",
];

</script>

<template>
  <div class="privacy-policy">
        <p class="text-sm text-gray-500 dark:text-gray-400">
          Last updated: {{ lastUpdated }}
        </p>
  
        <section class="pt-6">
          <p>
            <strong>Note:</strong> Fight Search is currently operated by two
            founders and is not yet operating under a separate legal entity.
          </p>
          <p>
            You can contact us about privacy at:
            <strong>info@fight-search.com</strong>.
          </p>
          <p>
            This Privacy Policy explains how we collect, use, store, and protect
            personal data when you use Fight Search.
          </p>
        </section>
  
        <section>
          <h2>1. Data We Collect</h2>
          <p>We collect the data needed to provide Fight Search.</p>
  
          <section v-for="category in dataCategories" :key="category.title">
            <h3>{{ category.title }}</h3>
            <ul>
              <li v-for="item in category.items" :key="item">{{ item }}</li>
            </ul>
          </section>
        </section>
  
        <section>
          <h2>2. How We Use Data</h2>
          <p>We use personal data to:</p>
          <ul>
            <li v-for="item in useCases" :key="item">{{ item }}</li>
          </ul>
        </section>
  
        <section>
          <h2>3. Legal Bases</h2>
          <p>Where GDPR applies, we process personal data based on:</p>
          <ul>
            <li v-for="item in legalBases" :key="item">{{ item }}</li>
          </ul>
        </section>
  
        <section>
          <h2>4. Verification Documents</h2>
          <p>Verification documents are used only for verification purposes.</p>
          <p>
            After a verification request reaches a final status, such as accepted,
            rejected, or expired, verification documents and related verification
            data are deleted after 30 days, unless we are legally required to keep
            them for longer.
          </p>
        </section>
  
        <section>
          <h2>5. File Uploads</h2>
          <p>
            Uploaded files, including avatars and verification documents, are
            stored using Backblaze B2.
          </p>
          <p>
            We use uploaded files only for the purpose for which they were
            provided, such as displaying avatars or reviewing verification
            requests.
          </p>
        </section>
  
        <section>
          <h2>6. Notifications</h2>
          <p>We may send service emails and push notifications related to:</p>
          <ul>
            <li>Account activity</li>
            <li>Verification status</li>
            <li>Fight offers</li>
            <li>Applications</li>
            <li>Messages</li>
            <li>Important service updates</li>
          </ul>
          <p>
            Emails are sent through our Supabase Edge Functions. Push
            notifications are sent using Firebase.
          </p>
          <p>
            You can disable push notifications through your device or browser
            settings.
          </p>
        </section>
  
        <section>
          <h2>7. Analytics And Error Logging</h2>
          <p>We use PostHog for product analytics and error logging.</p>
          <p>
            This helps us understand how Fight Search is used, detect bugs,
            improve the product, and maintain reliability.
          </p>
        </section>
  
        <section>
          <h2>8. Payments</h2>
          <p>
            Payments and subscriptions are not currently active, but we plan to
            add them using Stripe.
          </p>
          <p>
            When payments are enabled, Stripe will process payment information. We
            will not store full card details ourselves. This Privacy Policy should
            be updated before payments go live.
          </p>
        </section>
  
        <section>
          <h2>9. Who We Share Data With</h2>
          <p>
            We share data with service providers only where needed to operate
            Fight Search.
          </p>
          <p>These may include:</p>
          <ul>
            <li v-for="provider in providers" :key="provider">
              {{ provider }}
            </li>
          </ul>
          <p>We do not sell personal data.</p>
        </section>
  
        <section>
          <h2>10. Public Profiles</h2>
          <p>
            Currently, fighter, gym, and organisation profiles are not publicly
            visible to non-logged-in visitors.
          </p>
          <p>
            In the future, some profile information may become publicly visible,
            such as fighter, gym, or organisation profile pages. Before this is
            enabled, the app should clearly indicate which information will be
            public.
          </p>
        </section>
  
        <section>
          <h2>11. Data Retention</h2>
          <p>
            We keep personal data only for as long as needed for the purposes
            described in this policy.
          </p>
          <p>General retention principles:</p>
          <ul>
            <li>Account data is kept while your account is active.</li>
            <li>
              Fighter, gym, and organisation profiles are kept while they are
              active or needed for the service.
            </li>
            <li>
              Messages and fight offer records are kept while needed to provide
              the action center, dispute context, and service history.
            </li>
            <li>
              Verification documents and related verification data are deleted 30
              days after final verification status.
            </li>
            <li>
              Technical logs and analytics data are kept only as long as
              reasonably needed for security, debugging, and product improvement.
            </li>
          </ul>
        </section>
  
        <section>
          <h2>12. International Transfers</h2>
          <p>
            Some service providers may process data outside your country or
            outside the European Economic Area.
          </p>
          <p>
            Where required, we rely on appropriate safeguards, such as contractual
            protections, adequacy decisions, or other lawful transfer mechanisms.
          </p>
        </section>
  
        <section>
          <h2>13. Your Rights</h2>
          <p>Depending on where you live, you may have rights to:</p>
          <ul>
            <li v-for="right in userRights" :key="right">{{ right }}</li>
          </ul>
          <p>
            If you are in Slovenia, you can contact the Information Commissioner
            of the Republic of Slovenia.
          </p>
          <p>
            To exercise your rights, contact us at
            <strong>info@fight-search.com</strong>.
          </p>
        </section>
  
        <section>
          <h2>14. Account Deletion</h2>
          <p>You may request deletion of your account by contacting us.</p>
          <p>
            Some data may be retained where necessary for legal, security, fraud
            prevention, dispute resolution, or legitimate business purposes.
          </p>
        </section>
  
        <section>
          <h2>15. Children</h2>
          <p>Fight Search currently does not set a specific age limit.</p>
          <p>
            Because the app may involve combat sports, fighter profiles,
            verification, and public-facing sports data, we may introduce age
            restrictions or parental consent requirements in the future.
          </p>
        </section>
  
        <section>
          <h2>16. Automated Decision-Making</h2>
          <p>
            We do not currently make decisions that produce legal or similarly
            significant effects based solely on automated processing.
          </p>
        </section>
  
        <section>
          <h2>17. Security</h2>
          <p>
            We use technical and organisational measures to protect personal data,
            including access controls, authentication, and limited access to
            sensitive information.
          </p>
          <p>No online service can guarantee absolute security.</p>
        </section>
  
        <section>
          <h2>18. Changes To This Policy</h2>
          <p>We may update this Privacy Policy from time to time.</p>
          <p>
            If we make significant changes, we will notify users through the app
            or by email where appropriate.
          </p>
        </section>
  
        <section>
          <h2>19. Contact</h2>
          <p>
            Privacy contact:
            <strong>info@fight-search.com</strong>
          </p>
        </section>
      </div>
</template>

<style scoped>
.privacy-policy > section {
  margin-top: 3rem;
}

.privacy-policy > section:first-of-type {
  margin-top: 0;
}

.privacy-policy h2 {
  margin-bottom: 0rem;
  font-size: 1.75rem;
  font-weight: 800;
  letter-spacing: -0.025em;
}

.privacy-policy h3 {
  margin-top: 1rem;
  font-size: 1.15rem;
  font-weight: 700;
}

.privacy-policy p,
.privacy-policy ul {
  margin-top: 0.5rem;
}
</style>
